// users.router.js
//
// Три контекста по индустриальной практике (GitHub-стиль):
//   1. /users/:_id           — публичный read-only просмотр чужого профиля
//                              (контракт для community/organizations/etc. модулей).
//   2. /profile, /profile/*  — свой профиль/настройки/дашборд, БЕЗ ID в URL.
//                              Один компонент Profile/ProfileEdit/UserDashboard
//                              читает userId либо из route.params._id, либо
//                              из storeAuth.state.user._id (см. компоненты).
//   3. /backoffice/users/:_id/* — admin edit чужих юзеров. См. users.backoffice.router.js.
//
// edit/* и dashboard ИЗ-ПОД /users/:_id убраны: для чужого профиля настройки
// бессмысленны (своё → /profile/*, админское → /backoffice/users/:_id/*).

import * as auth from '@martyrs/src/modules/auth/views/store/auth.js';
import { useGlobalMixins } from '@martyrs/src/modules/core/views/mixins/mixins.js';
import { requiresAuth } from '../middlewares/auth.validation.js';
import { navigationItems } from '@martyrs/src/modules/auth/views/configs/navigation.user.config.js';
import HelpCard from '@martyrs/src/modules/core/views/components/blocks/HelpCard.vue';

const { isPhone, isTablet } = useGlobalMixins();

// Public read-only просмотр (`/users/:_id`).
// edit/* и dashboard здесь больше НЕ регистрируются — см. getProfileRoutes().
export function getUsersRoutes(options = {}) {
  const route = options.route || 'Home';

  return {
    parentName: route,
    config: {
      basePath: 'users',
      meta: {
        title: {
          en: 'Users',
          ru: 'Пользователи',
        },
      },
      routes: [
        {
          path: ':_id',
          name: 'User Profile Root',
          meta: {
            title: {
              en: 'Profile',
              ru: 'Профиль',
            },
            sidebar_hover: false,
            sidebar_navigation_items: navigationItems,
            sidebar_footer_component: HelpCard,
            sidebarOpenOnEnter: true,
            sidebarCloseOnLeave: true
          },
          children: [
            {
              path: '',
              name: 'User Profile',
              meta: {
                title: {
                  en: 'Profile',
                  ru: 'Профиль',
                },
                title_hide: true,
                hideNavigationBar: route => route.params._id === auth.state.user._id,
              },
              component: () => import(/* webpackChunkName: "profile" */ '../components/pages/Profile.vue'),
            },
          ],
        },
      ],
    }
  };
}

// Дочерние роуты ProfileEdit — одни и те же для обоих маунтов
// (/profile/edit/* и settings-маунт через getProfileSettingsRoutes).
// namePrefix разводит имена роутов между маунтами.
function getProfileEditChildren(namePrefix) {
  return [
    {
      path: '',
      // Явное имя: addRoutes префиксует name безусловно, безымянный child
      // получил бы имя "undefined" — два маунта затирали бы друг друга.
      name: `${namePrefix} Root`,
      // Redirect функцией от фактического пути, а не по имени и не строкой:
      // имя ломает routeNamePrefix из addRoutes, а относительная строка
      // резолвится по URL-правилам от родителя ('/x/settings' + 'profile'
      // → '/x/profile').
      redirect: to => ({ path: to.path.replace(/\/$/, '') + '/profile' }),
    },
    {
      path: 'profile',
      name: `${namePrefix} Profile`,
      component: () => import(/* webpackChunkName: "profile-edit-profile" */ '../components/pages/ProfileEditProfile.vue'),
    },
    {
      path: 'account',
      name: `${namePrefix} Account`,
      component: () => import(/* webpackChunkName: "profile-edit-account" */ '../components/pages/ProfileEditAccount.vue'),
    },
    {
      path: 'notifications',
      name: `${namePrefix} Notifications`,
      component: () => import(/* webpackChunkName: "profile-edit-notifications" */ '../components/pages/ProfileEditNotifications.vue'),
    },
  ];
}

// Свой профиль (`/profile`, `/profile/edit/*`, `/profile/dashboard`).
// Те же компоненты, что и под :_id-схемой, но без ID в URL.
// Внутри компонентов userId = useRoute().params._id || storeAuth.state.user._id.
export function getProfileRoutes(options = {}) {
  const route = options.route || 'Home';

  return {
    parentName: route,
    config: {
      basePath: 'profile',
      meta: {
        title: {
          en: 'Profile',
          ru: 'Профиль',
        },
      },
      routes: [
        {
          path: '',
          name: 'My Profile',
          beforeEnter: [requiresAuth],
          meta: {
            title: {
              en: 'Profile',
              ru: 'Профиль',
            },
            sidebar_hover: false,
            sidebar_navigation_items: navigationItems,
            sidebar_footer_component: HelpCard,
            sidebarOpenOnEnter: true,
            sidebarCloseOnLeave: true,
            title_hide: true,
          },
          component: () => import(/* webpackChunkName: "profile" */ '../components/pages/Profile.vue'),
        },
        {
          path: 'edit',
          name: 'My Profile Edit',
          beforeEnter: [requiresAuth],
          meta: {
            title: {
              en: 'Profile Settings',
              ru: 'Настройки профиля',
            },
            // Стабильный ключ для Client.vue — см. getProfileSettingsRoutes.
            layoutKey: 'profile-edit',
          },
          component: () => import(/* webpackChunkName: "profile-edit" */ '../components/pages/ProfileEdit.vue'),
          children: getProfileEditChildren('My Profile Edit'),
        },
        {
          path: 'dashboard',
          name: 'My Dashboard',
          beforeEnter: [requiresAuth],
          meta: {
            title: {
              en: 'Dashboard',
              ru: 'Дашборд',
            },
          },
          component: () => import(/* webpackChunkName: "profile-dashboard" */ '../components/pages/UserDashboard.vue'),
        },
      ],
    }
  };
}

// Настройки профиля вложенным роутом под роут проекта (settings-маунт).
// Проект задаёт родителя и путь через конфиг auth-модуля:
//   settings: { routeProfile: 'Dashboard', pathProfile: 'settings' }
// → /dashboard/settings/{profile,account,notifications}, inline-лейаут
// ProfileEdit (meta.profileEditInline), без popup и без /profile-страниц.
export function getProfileSettingsRoutes(options = {}) {
  return {
    parentName: options.route,
    config: {
      routes: [
        {
          path: options.path || 'settings',
          name: 'Profile Settings',
          beforeEnter: [requiresAuth],
          meta: {
            title: {
              en: 'Settings',
              ru: 'Настройки',
            },
            profileEditInline: true,
            // Client.vue ключует страницу по meta.layoutKey || route.name.
            // Табы — дочерние роуты с разными именами; без стабильного ключа
            // каждый клик по табу ремоунтил бы всю страницу с шапкой.
            layoutKey: 'profile-settings',
          },
          component: () => import(/* webpackChunkName: "profile-edit" */ '../components/pages/ProfileEdit.vue'),
          children: getProfileEditChildren('Profile Settings'),
        },
      ],
    }
  };
}
